import { Component, Input, ViewChild, forwardRef, ElementRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCalendar, MatCalendarCellCssClasses } from '@angular/material/datepicker';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(localizedFormat);
import localeData from 'dayjs/plugin/localeData';
dayjs.extend(localeData);

@Component({
	selector: 'bb-datepicker',
	templateUrl: './bb-datepicker.component.html',
	styleUrls: ['./bb-datepicker.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BbDatepickerComponent),
		multi: true
	}]
})

export class BbDatepickerComponent implements ControlValueAccessor, OnInit {
	@Input() minDate: Date;
	@Input() maxDate: Date;
	@Input() initialOpen: boolean;
	@Input() inline: boolean;
	@Input() initialDate: Date;
	@Input() rangeStartDate?: Date;
	@Input() rangeEndDate?: Date;
	@Input() theme?: string;
	@Input() label?: string;
	@Input() active?: boolean;
	@Input() stringFormat?: string;

	@ViewChild('matCalendar', { static: true }) calendar: MatCalendar<string>;

	dateString: string;
	isOpen: boolean;
	closeToBottom = false;
	private selectedDate: dayjs.Dayjs;
	private previouslySelectedDate: dayjs.Dayjs;
	private localeFormat: string;

	constructor(public element: ElementRef) {
		this.isOpen = false;
		this.localeFormat = dayjs().localeData().longDateFormat('L');
	}

	ngOnInit() {
		if (this.initialOpen) {
			this.isOpen = true;
		}

		this.calendar.selectedChange.subscribe((event: string) => {
			this.selectedDateChanged(dayjs(event));
		});

		if (this.initialDate) {
			this.selectedDateChanged(dayjs(this.initialDate));
		}
	}

	determinePanelPosition() {
		const hostRect = this.element?.nativeElement?.getBoundingClientRect();
		const bodyRect = document.body.getBoundingClientRect();
		this.closeToBottom = bodyRect?.height - hostRect?.y < 310;
	}

	open() {
		this.determinePanelPosition();
		this.calendar.updateTodaysDate(); // forces recheck dateClass()
		this.isOpen = true;
	}

	close() {
		this.isOpen = false;
	}

	// Conditionally adds a class to each cell in the current calendar view
	// ignore time differences
	dateClass() {
		return (date: Date): MatCalendarCellCssClasses => {
			if (!this.rangeEndDate || !this.rangeStartDate) {
				return;
			}

			const start = dayjs(this.rangeStartDate).startOf('day').toDate().toString();
			const end = dayjs(this.rangeEndDate).startOf('day').toDate().toString();
			const current = dayjs(date).startOf('day').toDate().toString();

			if (start === end) {
				return;
			}
			if (current === start) {
				return 'start-date-calendar';
			}
			if (current === end) {
				return 'end-date-calendar';
			}
			if (date > this.rangeStartDate && date < this.rangeEndDate) {
				return 'range-date-calendar';
			}
			return;
		};
	}

	updateDateFromInput() {
		const dayjsDate = dayjs(this.dateString, this.localeFormat);
		if (dayjsDate.isValid()) {
			this.selectedDateChanged(dayjsDate);
		}
	}

	selectedDateChanged(selectedDate: dayjs.Dayjs, skipPropagation = false) {
		this.close();
		if (selectedDate) {
			this.previouslySelectedDate = dayjs(this.selectedDate);
			this.selectedDate = selectedDate;
		}

		if (selectedDate && selectedDate !== this.previouslySelectedDate) {
			if (this.calendar.monthView) {
				this.calendar.monthView._init();
			}
			this.updateDateString();
			this.calendar.selected = dayjs(this.selectedDate).toISOString();
			this.calendar.startAt = this.calendar.selected;
			this.calendar.activeDate = this.calendar.selected;

			if (!skipPropagation) {
				const djs = dayjs(this.selectedDate);
				this.propagateChange(this.selectedDate ? (this.stringFormat ? djs.format(this.stringFormat) : djs.toDate()) : null);
			}
		}
	}

	writeValue(value: dayjs.Dayjs) {
		if (value) {
			if (this.maxDate && dayjs(value).toDate() > this.maxDate) {
				this.previouslySelectedDate = value;
				value = dayjs(this.maxDate);
			} else if (this.minDate && dayjs(value).toDate() < this.minDate) {
				this.previouslySelectedDate = value;
				value = dayjs(this.minDate);
			}
		}

		this.selectedDate = (value ? dayjs(value) : null);

		this.updateDateString();
		this.selectedDateChanged(this.selectedDate, true);
	}

	updateDateString() {
		if (dayjs(this.selectedDate).isValid()) {
			this.dateString = dayjs(this.selectedDate).format(this.localeFormat);
		}
	}

	onSelect(event: dayjs.Dayjs) {
		this.selectedDate = event;
		this.updateDateString();
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}
}
